import React, { useState, useEffect, useCallback, useRef } from "react";
import { useThemeMode } from "../ThemeContext";
import { fmt, fmtCompact, fmtK, timeAgo } from "../utils/financeFormat";
import {
  financeFetch, loadAccounts, loadLoans, loadCashPosition, loadOpenBills, loadPlaidItems,
  syncBalances, syncBills, syncTransactions, brokenPlaidItems as findBrokenItems, accountsToReview,
  debtPosition, billsSummary, netAfterScheduled as netAfter, loanPaymentsDue, depositAccounts as findDepositAccounts,
  LOAN_LABELS, cardFigures, loanFigures, AGING_BUCKETS, agingReport, billStatusLabel, scheduledAmount, billPartlyPaid,
  fmtBillDate, PNL_RANGES, pnlQuery, loadPnl, loadPnlBreakdown, loadPnlRows, pnlNotes, pnlLines,
  loadTransactions, sortTransactions, pageRange,
} from "../features/finance/financeData";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

// Reads, rules and syncs: features/finance/financeData.js (the phone's Finance page uses them too).
// apiFetch = financeFetch (the global fetch wrapper from config.js adds the API key).
const apiFetch = financeFetch;

// --- Colors ---
const C = {
  green: "#10b981", amber: "#fbbf24", red: "#f87171", blue: "#60a5fa",
  indigo: "#818cf8", indigoDark: "#6366f1", orange: "#fb923c",
};

// AGING_BUCKETS / getBucket: features/finance/financeData.

export default function FinancePage() {
  const { isDark } = useThemeMode();
  const [tab, setTab] = useState(0);

  const bg = isDark ? "#1a1a2e" : "#f8fafc";
  const cardBg = isDark ? "#16213e" : "#ffffff";
  const text = isDark ? "#e2e8f0" : "#1e293b";
  const textMuted = isDark ? "#94a3b8" : "#64748b";
  const textSecondary = isDark ? "#64748b" : "#94a3b8";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const borderStrong = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)";
  const inputBg = isDark ? "#0f172a" : "#f1f5f9";
  const shadow = isDark
    ? "0 1px 3px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.03)"
    : "0 1px 3px rgba(0,0,0,0.08)";
  const gridStroke = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const axisColor = isDark ? "#64748b" : "#94a3b8";

  const T = { bg, cardBg, text, textMuted, textSecondary, border, borderStrong, inputBg, shadow, gridStroke, axisColor };

  const tabs = ["Dashboard", "AP Aging", "P&L", "Transactions"];

  return (
    <div style={{ padding: 24, background: T.bg, minHeight: "100vh" }}>
      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `2px solid ${T.border}` }}>
        {tabs.map((label, i) => (
          <button
            key={label}
            onClick={() => setTab(i)}
            style={{
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: tab === i ? 600 : 400,
              color: tab === i ? C.indigo : T.textMuted,
              background: "transparent",
              border: "none",
              borderBottom: tab === i ? `2px solid ${C.indigo}` : "2px solid transparent",
              cursor: "pointer",
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 0 && <DashboardTab T={T} />}
      {tab === 1 && <ApAgingTab T={T} />}
      {tab === 2 && <PnlTab T={T} />}
      {tab === 3 && <TransactionsTab T={T} />}
    </div>
  );
}


// ============================================================
// TAB 0: DASHBOARD
// ============================================================
function DashboardTab({ T }) {
  const [accounts, setAccounts] = useState([]);
  const [cashPosition, setCashPosition] = useState(null);
  const [bills, setBills] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loanTotals, setLoanTotals] = useState(null);
  const [plaidItems, setPlaidItems] = useState([]);
  const [closingMissing, setClosingMissing] = useState(null);
  const [pickingItem, setPickingItem] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectMsg, setConnectMsg] = useState(null);
  const [reconnecting, setReconnecting] = useState(null);

  const load = useCallback(() => {
    loadAccounts().then(setAccounts).catch(() => {});
    loadLoans().then(r => { setLoans(r.loans); setLoanTotals(r.totals); }).catch(() => {});
    loadCashPosition().then(setCashPosition).catch(() => {});
    loadOpenBills().then(setBills).catch(() => {});
    loadPlaidItems().then(setPlaidItems).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncBalances(); // financeData
      load();
    } catch (e) { /* ignore */ }
    setSyncing(false);
  };

  const handleConnect = async () => {
    setConnectLoading(true);
    setConnectMsg(null);
    try {
      const tokenRes = await apiFetch("/finance/plaid/link-token", { method: "POST" });
      const linkToken = tokenRes?.data?.link_token;
      if (!linkToken) {
        setConnectMsg({ type: "error", text: "Failed to create link token. Is Plaid configured?" });
        setConnectLoading(false);
        return;
      }
      // Load Plaid Link script dynamically
      const openLink = (token) => {
        const handler = window.Plaid.create({
          token,
          onSuccess: async (publicToken, metadata) => {
            try {
              await apiFetch("/finance/plaid/exchange-token", {
                method: "POST",
                body: JSON.stringify({
                  public_token: publicToken,
                  institution_id: metadata.institution?.institution_id,
                  institution_name: metadata.institution?.name,
                }),
              });
              setConnectMsg({ type: "success", text: "Bank connected successfully!" });
              load();
            } catch (e) {
              setConnectMsg({ type: "error", text: `Exchange failed: ${e.message}` });
            }
            setConnectLoading(false);
          },
          onExit: (err) => {
            if (err) setConnectMsg({ type: "error", text: err.error_message });
            setConnectLoading(false);
          },
        });
        handler.open();
      };
      if (!window.Plaid) {
        const script = document.createElement("script");
        script.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
        script.onload = () => openLink(linkToken);
        document.head.appendChild(script);
      } else {
        openLink(linkToken);
      }
    } catch (e) {
      setConnectMsg({ type: "error", text: e.message });
      setConnectLoading(false);
    }
  };

  // Retire the accounts Plaid has stopped reporting on this item. A sync only ever
  // FLAGS them — deciding an account is really closed moves the cash and debt
  // totals, so it is a person's call and this is where they make it.
  const handleCloseMissing = async (item) => {
    const dead = accounts.filter(a => a.plaid_missing_since && a.is_active !== false);
    if (dead.length === 0) return;
    setClosingMissing(item.id);
    setConnectMsg(null);
    try {
      for (const a of dead) {
        await apiFetch(`/finance/accounts/${a.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: false }),
        });
      }
      setConnectMsg({
        type: "success",
        text: `Marked ${dead.map(a => `***${a.mask}`).join(", ")} closed — they no longer count toward cash or debt.`,
      });
      load();
    } catch (e) {
      setConnectMsg({ type: "error", text: `Could not close the account: ${e.message}` });
    }
    setClosingMissing(null);
  };

  // accountSelection=true adds Plaid's Account Select step. Credential repair alone
  // can never pull in an account opened after the item was linked — that is how the
  // refinanced shop-building loan stayed invisible from July to September 2026.
  const handleReconnect = async (item, accountSelection = false) => {
    setReconnecting(item.id);
    setConnectMsg(null);
    try {
      const tokenRes = await apiFetch(
        `/finance/plaid/items/${item.id}/update-link-token${accountSelection ? "?account_selection=true" : ""}`,
        { method: "POST" });
      const linkToken = tokenRes?.data?.link_token;
      if (!linkToken) {
        setConnectMsg({ type: "error", text: `Failed to create update link token for ${item.institution_name}.` });
        setReconnecting(null);
        return;
      }
      const openLink = (token) => {
        const handler = window.Plaid.create({
          token,
          onSuccess: async () => {
            // Update mode preserves the existing access_token — no public token to exchange.
            // Reactivate flips kc_plaid_items.status back to 'active' and runs an immediate
            // balance sync so the new numbers show up without waiting for the cron tick.
            try {
              const r = await apiFetch(
                `/finance/plaid/items/${item.id}/reactivate${accountSelection ? "?account_selection=true" : ""}`,
                { method: "POST" });
              const added = r?.accounts_added || [];
              setConnectMsg({
                type: "success",
                text: added.length
                  ? `${item.institution_name}: added ${added.map(m => `***${m}`).join(", ")}. Run a transaction backfill to pull its history.`
                  : `${item.institution_name} reconnected${r?.synced_accounts ? ` — refreshed ${r.synced_accounts} account${r.synced_accounts === 1 ? "" : "s"}` : ""}.`,
              });
              load();
            } catch (e) {
              setConnectMsg({ type: "error", text: `Reactivation failed for ${item.institution_name}: ${e.message}` });
            }
            setReconnecting(null);
          },
          onExit: (err) => {
            if (err) setConnectMsg({ type: "error", text: err.error_message || "Plaid Link exited" });
            setReconnecting(null);
          },
        });
        handler.open();
      };
      if (!window.Plaid) {
        const script = document.createElement("script");
        script.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
        script.onload = () => openLink(linkToken);
        document.head.appendChild(script);
      } else {
        openLink(linkToken);
      }
    } catch (e) {
      setConnectMsg({ type: "error", text: `Reconnect failed: ${e.message}` });
      setReconnecting(null);
    }
  };

  const brokenPlaidItems = findBrokenItems(plaidItems); // financeData
  // Healthy items that still need a human: Plaid says the bank holds an account we
  // are not authorized for, or an account we hold has stopped being reported (a
  // payoff or refinance). Both are fixed by the same Account Select flow.
  const itemsNeedingAccounts = accountsToReview(plaidItems); // financeData

  // ---- Debt, counted once -----------------------------------------------------
  // A loan appears twice in our data: as a row in kc_equipment_loans (terms, the
  // recorded balance) and, for the Labette ones, as a Plaid account (live balance).
  // Reconciling the two USED to happen here, in a copy of a rule the API also had
  // its own version of — and the two disagreed by about $400K. The API owns it now
  // (`_kc_debt_position`) and returns `display_balance` / `live_mask` / `stale_mask`
  // per loan plus the totals. This file renders them; it does not re-derive them.
  // financeData: debt counted once, which Plaid loan accounts no loan row claims, the bills' sums.
  const {
    loanAccounts, creditAccounts, uniqueLoanAccounts, totalLoanBalance, totalMonthly, totalDebt,
  } = debtPosition({ accounts, loans, loanTotals });
  const { overdueBills, overdueAmount, totalAP, scheduledBills, scheduledTotal } = billsSummary(bills);
  const availableBalance = cashPosition ? (Number(cashPosition.total_available) || 0) : 0;
  const netAfterScheduled = netAfter(cashPosition, scheduledTotal);

  return (
    <div>
      {/* Plaid connection-lost banner — one row per broken item, prominent at top so
          operators see stale balances are stale before they trust the numbers. Mirrors
          the equivalent banner on finance.badideaco.com so the two finance dashboards
          surface the same state in the same shape. */}
      {brokenPlaidItems.length > 0 && (
        <div style={{
          background: "rgba(251,191,36,0.12)",
          border: `1px solid ${C.amber}`,
          borderRadius: 10,
          padding: "14px 18px",
          marginBottom: 24,
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          {brokenPlaidItems.map((item, idx) => (
            <div key={item.id} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16,
              paddingTop: idx > 0 ? 10 : 0,
              borderTop: idx > 0 ? `1px solid rgba(251,191,36,0.25)` : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <span style={{
                  flexShrink: 0, fontSize: 18, lineHeight: 1,
                  width: 28, height: 28, borderRadius: "50%",
                  background: "rgba(251,191,36,0.2)", color: C.amber,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700,
                }}>!</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.amber, letterSpacing: "-0.01em" }}>
                    {item.institution_name || "Bank"} &mdash; Connection Lost
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                    Bank login credentials have changed. Balances are stale until you re-authenticate.
                    {item.error_code && (
                      <span style={{ marginLeft: 8, fontFamily: "monospace", fontSize: 11, color: T.textSecondary }}>
                        ({item.error_code})
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleReconnect(item)}
                disabled={reconnecting === item.id}
                style={{
                  flexShrink: 0,
                  background: reconnecting === item.id ? "rgba(251,191,36,0.5)" : C.amber,
                  color: "#1a1a2e",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: reconnecting === item.id ? "default" : "pointer",
                  transition: "background 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {reconnecting === item.id ? "Reconnecting…" : "Reconnect"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Accounts the bank has but Plaid has not been authorized for, and accounts
          Plaid has stopped reporting. Both mean a number on this page is not what
          the bank thinks. The button runs Link update mode with Account Select —
          the only flow that can add an account opened after the initial link. */}
      {itemsNeedingAccounts.length > 0 && (
        <div style={{
          background: "rgba(96,165,250,0.10)",
          border: `1px solid ${C.blue}`,
          borderRadius: 10, padding: "14px 18px", marginBottom: 24,
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          {itemsNeedingAccounts.map((item, idx) => (
            <div key={item.id} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16,
              paddingTop: idx > 0 ? 10 : 0,
              borderTop: idx > 0 ? `1px solid rgba(96,165,250,0.25)` : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <span style={{
                  flexShrink: 0, fontSize: 16, lineHeight: 1, width: 28, height: 28, borderRadius: "50%",
                  background: "rgba(96,165,250,0.2)", color: C.blue,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
                }}>+</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.blue, letterSpacing: "-0.01em" }}>
                    {item.institution_name || "Bank"} &mdash; accounts need review
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                    {item.new_accounts_available_at
                      ? "The bank has an account this connection isn't authorized for. "
                      : ""}
                    {item.missing_account_count > 0
                      ? `${item.missing_account_count} account${item.missing_account_count === 1 ? " is" : "s are"} no longer reported by the bank (closed or refinanced) — the balance shown is frozen. `
                      : ""}
                    Re-authorize and pick the accounts to include.
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              {item.missing_account_count > 0 && (
                <button
                  onClick={() => handleCloseMissing(item)}
                  disabled={closingMissing === item.id}
                  title="Mark the accounts the bank stopped reporting as closed, so they leave the totals"
                  style={{
                    background: "transparent", color: T.textMuted,
                    border: `1px solid ${T.border}`, padding: "8px 14px", borderRadius: 6,
                    fontSize: 13, fontWeight: 600,
                    cursor: closingMissing === item.id ? "default" : "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {closingMissing === item.id ? "Closing\u2026" : "Mark closed"}
                </button>
              )}
              <button
                onClick={() => handleReconnect(item, true)}
                disabled={reconnecting === item.id}
                style={{
                  flexShrink: 0,
                  background: reconnecting === item.id ? "rgba(96,165,250,0.5)" : C.blue,
                  color: "#0b1020", border: "none", padding: "8px 16px", borderRadius: 6,
                  fontSize: 13, fontWeight: 600,
                  cursor: reconnecting === item.id ? "default" : "pointer", whiteSpace: "nowrap",
                }}
              >
                {reconnecting === item.id ? "Opening\u2026" : "Add accounts"}
              </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cash Position */}
      {cashPosition && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
          <MetricCard T={T} label="Total Cash" value={fmt(cashPosition.total_cash)} color={C.green} />
          <MetricCard T={T} label="Available" value={fmt(cashPosition.total_available)} color={C.blue} />
          {/* Was: cash-position debt (Plaid loan/credit accounts) + every non-Labette
              loan row. That took ALL Labette debt from Plaid, so when the building
              loan was refinanced out of Plaid's account list its balance silently
              left Total Debt entirely. Now every loan counts at its best-known
              balance, live or recorded, and each exactly once. */}
          <MetricCard T={T} label="Total Debt" value={fmt(totalDebt)} color={C.red} subtitle={`${loans.length} loans + ${creditAccounts.length} card${creditAccounts.length === 1 ? "" : "s"}`} />
          <MetricCard T={T} label="AP Outstanding" value={fmt(totalAP)} color={C.amber} subtitle={`${bills.length} unpaid bills`} />
          {overdueBills.length > 0 && (
            <MetricCard T={T} label="Overdue Bills" value={overdueBills.length.toString()} color={C.red} subtitle={fmt(overdueAmount)} />
          )}
          {(() => {
            const { upcoming, total } = loanPaymentsDue(loans); // financeData
            if (upcoming.length === 0) return null;
            return <MetricCard T={T} label="Loan Payments Due" value={fmt(total)} color={C.red} subtitle={`${upcoming.length} payment${upcoming.length !== 1 ? "s" : ""} within 7 days`} />;
          })()}
        </div>
      )}

      {/* Net Cash Position after scheduled payments */}
      {cashPosition && (
        <div style={{
          background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow,
          border: `1px solid ${T.border}`, marginBottom: 24,
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>Net Cash Position</h2>
          <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ textAlign: "center", minWidth: 140 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>Available Balance</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: C.blue }}>{fmt(availableBalance)}</div>
            </div>
            <div style={{ fontSize: 26, color: T.textMuted, fontWeight: 300 }}>&minus;</div>
            <div style={{ textAlign: "center", minWidth: 140 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>
                Scheduled Payments
                <span style={{ marginLeft: 6, fontSize: 11, color: T.textSecondary }}>({scheduledBills.length} bill{scheduledBills.length !== 1 ? "s" : ""})</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: C.amber }}>{fmt(scheduledTotal)}</div>
            </div>
            <div style={{ fontSize: 26, color: T.textMuted, fontWeight: 300 }}>=</div>
            <div style={{ textAlign: "center", minWidth: 140 }}>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>Net After Payments</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: netAfterScheduled >= 0 ? C.green : C.red }}>
                {fmt(netAfterScheduled)}
              </div>
            </div>
          </div>
          {scheduledBills.length > 0 && (
            <div style={{ marginTop: 16, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, marginBottom: 8 }}>Scheduled Payments</div>
              {scheduledBills
                .sort((a, b) => (Number(b.remaining_balance || b.amount) || 0) - (Number(a.remaining_balance || a.amount) || 0))
                .map((bill, i) => (
                <div key={bill.bill_com_id || i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "6px 0", borderBottom: i < scheduledBills.length - 1 ? `1px solid ${T.border}` : "none",
                }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{bill.vendor_name}</span>
                    {bill.invoice_number && (
                      <span style={{ fontSize: 12, color: T.textMuted, marginLeft: 8 }}>#{bill.invoice_number}</span>
                    )}
                    {bill.scheduled_payment_date && (
                      <span style={{ fontSize: 11, color: T.textSecondary, marginLeft: 8 }}>
                        pays {new Date(bill.scheduled_payment_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.amber }}>{fmt(scheduledAmount(bill))}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bank Accounts Grid — depository/checking only */}
      {(() => {
        const depositAccounts = findDepositAccounts(accounts); // financeData (+ LOAN_LABELS, CREDIT_LABELS)
        return (<>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: T.text, margin: 0 }}>Bank Accounts</h2>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleSync} disabled={syncing} style={btnStyle(C.indigo, syncing)}>
                {syncing ? "Syncing..." : "Sync Balances"}
              </button>
              {/* A permanent home for this. It used to exist only inside the
                  "accounts need review" banner, so the one routine reason to reach
                  for it — the bank opened an account after we linked — left you with
                  no button at all unless something was ALSO wrong. Plaid fixes an
                  item's account list at link time; this is the only flow that can
                  add one, and it is maintenance, not an error path. */}
              {plaidItems.length > 0 && (
                <button
                  onClick={() => plaidItems.length === 1
                    ? handleReconnect(plaidItems[0], true)
                    : setPickingItem(v => !v)}
                  disabled={reconnecting !== null}
                  title="The bank has an account this connection isn't authorized for (a refinanced loan, a new card)"
                  style={btnStyle(C.blue, reconnecting !== null)}
                >
                  {reconnecting !== null ? "Opening\u2026" : "Add accounts"}
                </button>
              )}
              <button onClick={handleConnect} disabled={connectLoading} style={btnStyle(C.green, connectLoading)}>
                {connectLoading ? "Connecting..." : "Connect Bank"}
              </button>
            </div>
          </div>

          {/* Which bank? Only asked when more than one is linked. */}
          {pickingItem && plaidItems.length > 1 && (
            <div style={{
              display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
              padding: "10px 14px", marginBottom: 12, borderRadius: 8,
              background: "rgba(96,165,250,0.10)", border: `1px solid ${C.blue}`,
            }}>
              <span style={{ fontSize: 13, color: T.textMuted }}>
                Re-authorize which bank, and pick the accounts to include?
              </span>
              {plaidItems.map(item => (
                <button key={item.id}
                  onClick={() => { setPickingItem(false); handleReconnect(item, true); }}
                  disabled={reconnecting === item.id}
                  style={btnStyle(C.blue, reconnecting === item.id)}
                >
                  {item.institution_name || "Bank"}
                </button>
              ))}
              <button onClick={() => setPickingItem(false)}
                style={{ background: "transparent", color: T.textMuted, border: `1px solid ${T.border}`,
                         padding: "8px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          )}

          {connectMsg && (
            <div style={{
              padding: "8px 16px", borderRadius: 8, marginBottom: 12, fontSize: 13, fontWeight: 500,
              background: connectMsg.type === "error" ? "rgba(248,113,113,0.1)" : "rgba(16,185,129,0.1)",
              color: connectMsg.type === "error" ? C.red : C.green,
            }}>
              {connectMsg.text}
            </div>
          )}

          {depositAccounts.length === 0 && accounts.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: T.textMuted, background: T.cardBg, borderRadius: 12, border: `1px solid ${T.border}`, marginBottom: 24 }}>
              No bank accounts connected. Click "Connect Bank" to link your accounts via Plaid.
            </div>
          ) : (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
              {depositAccounts.map(a => (
                <div key={a.id} style={{
                  background: T.cardBg, borderRadius: 10, padding: 16, boxShadow: T.shadow,
                  border: `1px solid ${T.border}`, flex: "1 1 220px", minWidth: 220, maxWidth: 320,
                }}>
                  <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 2 }}>{a.institution_name}</div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 8 }}>
                    {a.account_name} {a.mask ? `***${a.mask}` : ""}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.indigo }}>{fmt(a.current_balance)}</div>
                  {a.available_balance != null && a.available_balance !== a.current_balance && (
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                      Available: {fmt(a.available_balance)}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>
                    {a.account_type} {a.last_synced ? `\u00B7 ${timeAgo(a.last_synced)}` : ""}
                  </div>
                  {a.plaid_missing_since && (
                    <div style={{ fontSize: 11, color: C.amber, marginTop: 4, fontWeight: 500 }}>
                      No longer reported by the bank &mdash; balance frozen since {timeAgo(a.plaid_missing_since)}.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Credit Cards — Plaid credit accounts (revolving debt, not term loans) */}
          {creditAccounts.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={{ fontSize: 16, fontWeight: 600, color: T.text, margin: 0 }}>Credit Cards</h2>
                <span style={{ fontSize: 12, color: T.textMuted }}>
                  {fmt(creditAccounts.reduce((s, a) => s + Math.abs(Number(a.current_balance) || 0), 0))} total balance
                </span>
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {creditAccounts.map(a => {
                  // financeData: Plaid's remaining credit, else limit - balance; % of the limit used.
                  const { label, balance, limit, available, pctUsed } = cardFigures(a);
                  return (
                    <div key={a.id} style={{
                      background: T.cardBg, borderRadius: 10, padding: 16, boxShadow: T.shadow,
                      border: `1px solid ${T.border}`, flex: "1 1 260px", minWidth: 260, maxWidth: 340,
                      borderLeft: `3px solid ${C.red}`,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontSize: 12, color: T.textMuted }}>{a.institution_name}</div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginTop: 2 }}>
                            {label.name || a.account_name} {a.mask ? `***${a.mask}` : ""}
                          </div>
                        </div>
                        <span style={{
                          fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600,
                          background: "rgba(248,113,113,0.15)", color: C.red,
                        }}>Credit</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: C.red, marginTop: 4 }}>{fmt(balance)}</div>
                      {available != null && (
                        <div style={{ fontSize: 12, color: C.green, marginTop: 2, fontWeight: 500 }}>
                          {fmt(available)} available credit
                        </div>
                      )}
                      {limit > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textMuted, marginBottom: 3 }}>
                            <span>{pctUsed.toFixed(1)}% utilized</span>
                            <span>{fmt(limit)} limit</span>
                          </div>
                          <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                            <div style={{
                              height: "100%", width: `${pctUsed}%`,
                              background: pctUsed > 80 ? C.red : pctUsed > 50 ? C.amber : C.green,
                              borderRadius: 2, transition: "width 0.3s",
                            }} />
                          </div>
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: T.textSecondary, marginTop: 6 }}>
                        Plaid synced {a.last_synced ? timeAgo(a.last_synced) : "never"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Loans & Debt — combines equipment loans + Plaid loan accounts */}
          {(loans.length > 0 || uniqueLoanAccounts.length > 0) && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={{ fontSize: 16, fontWeight: 600, color: T.text, margin: 0 }}>Loans &amp; Debt</h2>
                <span style={{ fontSize: 12, color: T.textMuted }}>
                  {fmt(totalLoanBalance)} total {totalMonthly > 0 ? `\u00B7 ${fmt(totalMonthly)}/mo` : ""}
                </span>
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {loans.map(loan => {
                  // financeData.loanFigures: `stale_mask` is already null whenever something live answered
                  // (the API's resolver), and the bar comes from the balance printed above it, clamped 0-100
                  // (taking pct_paid from the server while showing a Plaid balance once read "$0.00" and
                  // "3.5% paid"; a negative width renders the bar FULL).
                  const {
                    orig, isUrgent, isLOC, plaidMatch, stale, displayBalance, pctPaid, locAvailable, notesWarn, nextPaymentText,
                  } = loanFigures(loan, loanAccounts);
                  return (
                    <div key={loan.id} style={{
                      background: T.cardBg, borderRadius: 10, padding: 16, boxShadow: T.shadow,
                      border: `1px solid ${T.border}`, flex: "1 1 280px", minWidth: 280, maxWidth: 360,
                      borderLeft: `3px solid ${isLOC ? C.blue : C.orange}`,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontSize: 12, color: T.textMuted }}>{loan.lender} {loan.contract_number}</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginTop: 2 }}>{loan.description}</div>
                        </div>
                        <span style={{
                          fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600,
                          background: isLOC ? "rgba(96,165,250,0.15)" : "rgba(251,191,36,0.15)",
                          color: isLOC ? C.blue : C.amber,
                        }}>{isLOC ? "LOC" : "Term"}</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: C.orange, marginTop: 4 }}>{fmt(displayBalance)}</div>
                      {/* The operator's own note. It was written into the record and
                          then never shown, so things that only a person knows — a
                          refinance, a draft the bank never set up — stayed invisible
                          on the one page meant to show them. */}
                      {loan.notes && (
                        <div title={loan.notes} style={{
                          fontSize: 11, color: notesWarn ? C.amber : T.textMuted,
                          marginTop: 4, lineHeight: 1.35,
                          display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}>
                          {loan.notes}
                        </div>
                      )}
                      {stale && (
                        <div style={{ fontSize: 11, color: C.amber, marginTop: 2, fontWeight: 500 }}>
                          Bank stopped reporting ***{stale.mask} &mdash; showing the recorded balance, not a live one.
                        </div>
                      )}
                      {isLOC && (() => {
                        const avail = locAvailable;
                        return avail > 0 ? (
                          <div style={{ fontSize: 12, color: C.green, marginTop: 2, fontWeight: 500 }}>
                            {fmt(avail)} available credit
                            {/* What a revolving line actually has. It has no "% paid" —
                                you draw it back down — so the API sends null for that
                                and this instead. */}
                            {loan.utilization_pct != null && (
                              <span style={{ color: T.textMuted, fontWeight: 400 }}>
                                {" \u00B7 "}{loan.utilization_pct}% drawn
                              </span>
                            )}
                          </div>
                        ) : null;
                      })()}
                      {!isLOC && orig > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textMuted, marginBottom: 3 }}>
                            <span>{pctPaid.toFixed(1)}% paid</span>
                            <span>{fmt(orig)} original</span>
                          </div>
                          <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                            <div style={{
                              height: "100%", width: `${Math.min(pctPaid, 100)}%`,
                              background: C.orange, borderRadius: 2, transition: "width 0.3s",
                            }} />
                          </div>
                        </div>
                      )}
                      {/* Detail grid */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: 8, fontSize: 11 }}>
                        {loan.interest_rate > 0 && (<>
                          <span style={{ color: T.textMuted }}>Rate</span>
                          <span style={{ color: T.text, fontWeight: 500, textAlign: "right" }}>{(loan.interest_rate * 100).toFixed(2)}%</span>
                        </>)}
                        <span style={{ color: T.textMuted }}>Payment</span>
                        <span style={{ color: T.text, fontWeight: 500, textAlign: "right" }}>{loan.monthly_payment > 0 ? `${fmt(loan.monthly_payment)}/mo` : "Interest only"}</span>
                        {loan.maturity_date && (<>
                          <span style={{ color: T.textMuted }}>Matures</span>
                          <span style={{ color: T.text, fontWeight: 500, textAlign: "right" }}>{loan.maturity_date}</span>
                        </>)}
                        {loan.payment_account && (<>
                          <span style={{ color: T.textMuted }}>Paid from</span>
                          <span style={{ color: T.text, fontWeight: 500, textAlign: "right" }}>{loan.payment_account}</span>
                        </>)}
                      </div>
                      {loan.next_payment_date && (
                        <div style={{
                          fontSize: 11, marginTop: 6, padding: "4px 8px", borderRadius: 4,
                          background: isUrgent ? "rgba(248,113,113,0.1)" : "transparent",
                          color: isUrgent ? C.red : T.textMuted,
                          fontWeight: isUrgent ? 600 : 400,
                        }}>
                          {nextPaymentText}
                        </div>
                      )}
                      {plaidMatch && (
                        <div style={{ fontSize: 10, color: T.textSecondary, marginTop: 4 }}>
                          Plaid synced {timeAgo(plaidMatch.last_synced)}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Plaid loan accounts not yet in the equipment loans table */}
                {uniqueLoanAccounts.map(a => {
                  const label = LOAN_LABELS[a.mask] || {};
                  return (
                    <div key={a.id} style={{
                      background: T.cardBg, borderRadius: 10, padding: 16, boxShadow: T.shadow,
                      border: `1px solid ${T.border}`, flex: "1 1 260px", minWidth: 260, maxWidth: 340,
                      borderLeft: `3px solid ${C.orange}`,
                    }}>
                      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 2 }}>{a.institution_name}</div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 8 }}>
                        {label.name || a.account_name} {a.mask ? `***${a.mask}` : ""}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: C.orange }}>{fmt(Math.abs(Number(a.current_balance) || 0))}</div>
                      <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>
                        {label.type || "loan"} {a.last_synced ? `\u00B7 ${timeAgo(a.last_synced)}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>);
      })()}
    </div>
  );
}


// ============================================================
// TAB 1: AP AGING
// ============================================================
function StatusBadge({ status }) {
  const colors = {
    UNPAID: { bg: "#fef3c7", color: "#92400e", border: "#fbbf24" },
    SCHEDULED: { bg: "#dbeafe", color: "#1e40af", border: "#60a5fa" },
    PARTIALLY_PAID: { bg: "#e0e7ff", color: "#4338ca", border: "#818cf8" },
    OVERDUE: { bg: "#fee2e2", color: "#991b1b", border: "#f87171" },
  };
  const s = colors[status] || colors.UNPAID;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: "nowrap",
    }}>
      {billStatusLabel(status || "UNPAID")}
    </span>
  );
}

function ApAgingTab({ T }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expandedVendors, setExpandedVendors] = useState({});

  const toggleVendor = (bucketKey, vendorName) => {
    const key = `${bucketKey}::${vendorName}`;
    setExpandedVendors(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const load = useCallback(() => {
    setLoading(true);
    loadOpenBills() // financeData
      .then(setBills)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncBills(); // financeData
      load();
    } catch (e) { /* ignore */ }
    setSyncing(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>Loading...</div>;

  // Bills by bucket, each bucket's vendors (most owed first), the total: financeData.agingReport.
  const { buckets, vendorsByBucket, grandTotal } = agingReport(bills);

  const chartData = AGING_BUCKETS.map(b => ({
    name: b.label,
    amount: Math.round(buckets[b.key].total * 100) / 100,
    count: buckets[b.key].count,
  }));
  const fmtDate = fmtBillDate; // financeData

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text, margin: 0 }}>AP Aging Report</h1>
        <button onClick={handleSync} disabled={syncing} style={btnStyle(C.indigo, syncing)}>
          {syncing ? "Syncing..." : "Sync Bills"}
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        {AGING_BUCKETS.map(b => (
          <div key={b.key} style={{
            background: T.cardBg, borderRadius: 10, padding: 16, boxShadow: T.shadow,
            border: `1px solid ${T.border}`, borderLeft: `3px solid ${b.color}`,
            flex: "1 1 150px", minWidth: 150,
          }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>{b.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: b.color }}>{fmt(buckets[b.key].total)}</div>
            <div style={{ fontSize: 11, color: T.textSecondary }}>
              {buckets[b.key].count} bill{buckets[b.key].count !== 1 ? "s" : ""}
            </div>
          </div>
        ))}
        <div style={{
          background: C.indigoDark, borderRadius: 10, padding: 16, boxShadow: T.shadow,
          border: `1px solid ${T.border}`, flex: "1 1 150px", minWidth: 150,
        }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>Total Outstanding</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>{fmt(grandTotal)}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
            {bills.length} bill{bills.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Bar chart */}
      <div style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow, border: `1px solid ${T.border}`, marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>Aging Distribution</h2>
        <div style={{ height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.gridStroke} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: T.axisColor }} />
              <YAxis tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`} tick={{ fontSize: 12, fill: T.axisColor }} />
              <Tooltip
                formatter={v => fmt(v)}
                contentStyle={{ background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text }}
              />
              <Bar dataKey="amount" name="Amount" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={AGING_BUCKETS[i].color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Vendor breakdown with expandable bill details */}
      {AGING_BUCKETS.filter(b => buckets[b.key].count > 0).map(b => (
        <div key={b.key} style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow, border: `1px solid ${T.border}`, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: b.color, marginRight: 8, verticalAlign: "middle" }} />
            {b.label} — {fmt(buckets[b.key].total)}
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 28 }} />
              <col />
              <col style={{ width: 140 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 140 }} />
            </colgroup>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.borderStrong}` }}>
                <th style={{ ...thStyle(T), width: 28 }} />
                <th style={thStyle(T)}>Vendor</th>
                <th style={{ ...thStyle(T), textAlign: "right" }}>Amount</th>
                <th style={{ ...thStyle(T), textAlign: "center" }}>Bills</th>
                <th style={{ ...thStyle(T), textAlign: "center" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {vendorsByBucket[b.key].map(v => {
                const vKey = `${b.key}::${v.name}`;
                const isExpanded = expandedVendors[vKey];
                const statuses = [...new Set(v.bills.map(bl => bl.status))];
                return (
                  <React.Fragment key={v.name}>
                    <tr
                      onClick={() => toggleVendor(b.key, v.name)}
                      style={{ borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}
                    >
                      <td style={{ ...tdStyle(T), fontSize: 11, color: T.textMuted, textAlign: "center" }}>
                        {isExpanded ? "\u25BC" : "\u25B6"}
                      </td>
                      <td style={{ ...tdStyle(T), fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.name}</td>
                      <td style={{ ...tdStyle(T), fontWeight: 600, textAlign: "right", color: b.color }}>{fmt(v.total)}</td>
                      <td style={{ ...tdStyle(T), textAlign: "center", color: T.textMuted }}>{v.count}</td>
                      <td style={{ ...tdStyle(T), textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                          {statuses.map(s => <StatusBadge key={s} status={s} />)}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && v.bills
                      .sort((a, bb) => (Number(bb.remaining_balance || bb.amount) || 0) - (Number(a.remaining_balance || a.amount) || 0))
                      .map((bill, idx) => (
                      <tr key={bill.bill_com_id || idx} style={{ borderBottom: `1px solid ${T.border}`, background: T.inputBg }}>
                        <td style={tdStyle(T)} />
                        <td style={{ ...tdStyle(T), paddingLeft: 28 }}>
                          <div style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {bill.invoice_number && (
                              <span style={{ fontWeight: 500 }}>#{bill.invoice_number}</span>
                            )}
                            {bill.description && (
                              <span style={{ color: T.textMuted, marginLeft: bill.invoice_number ? 8 : 0 }}>
                                {bill.description.length > 60 ? bill.description.slice(0, 60) + "..." : bill.description}
                              </span>
                            )}
                            {!bill.invoice_number && !bill.description && (
                              <span style={{ color: T.textMuted, fontStyle: "italic" }}>No description</span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                            {bill.bill_date && <span>Billed {fmtDate(bill.bill_date)}</span>}
                            {bill.bill_date && bill.due_date && <span> &middot; </span>}
                            {bill.due_date && <span>Due {fmtDate(bill.due_date)}</span>}
                          </div>
                        </td>
                        <td style={{ ...tdStyle(T), textAlign: "right", fontWeight: 500 }}>
                          {fmt(Number(bill.remaining_balance || bill.amount) || 0)}
                          {billPartlyPaid(bill) && (
                            <div style={{ fontSize: 11, color: T.textMuted }}>of {fmt(bill.amount)}</div>
                          )}
                        </td>
                        <td style={{ ...tdStyle(T), textAlign: "center" }} />
                        <td style={{ ...tdStyle(T), textAlign: "center" }}>
                          <StatusBadge status={bill.status} />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {bills.length === 0 && (
        <div style={{ padding: 32, textAlign: "center", color: T.textSecondary, background: T.cardBg, borderRadius: 12, border: `1px solid ${T.border}` }}>
          No unpaid bills found. Sync from Bill.com to see AP data.
        </div>
      )}
    </div>
  );
}


// ============================================================
// TAB 2: P&L
// ============================================================
// Period choices for the P&L. YTD is the default (Mike, 2026-09-30); the API takes
// `period=ytd` or `months=N`, and the two drill-down routes take the same query so a
// category always sums to its line (jobq-api kc_pnl.py).
// PNL_RANGES / pnlQuery / loadPnl / loadPnlBreakdown / loadPnlRows / pnlNotes: features/finance/financeData.

function PnlTab({ T }) {
  const [pnl, setPnl] = useState(null);
  const [range, setRange] = useState("ytd");
  const [loading, setLoading] = useState(true);
  // Drill-down, mirroring the Bad Idea finance P&L: line -> category -> transaction, each level
  // fetched the first time it is opened and cached until the period changes.
  const [openLines, setOpenLines] = useState(new Set());      // "revenue" | "invoiced" | "opex"
  const [breakdown, setBreakdown] = useState({});             // line -> { loading, error, categories }
  const [openCats, setOpenCats] = useState(new Set());        // `${line}|${key}`
  const [catTx, setCatTx] = useState({});                     // `${line}|${key}` -> { loading, error, transactions }
  const rangeQuery = pnlQuery(range);

  useEffect(() => {
    setLoading(true);
    setOpenLines(new Set()); setBreakdown({}); setOpenCats(new Set()); setCatTx({});
    loadPnl(rangeQuery)
      .then(data => { if (data) setPnl(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [rangeQuery]);

  const toggleLine = (line) => {
    const next = new Set(openLines);
    if (next.has(line)) { next.delete(line); setOpenLines(next); return; }
    next.add(line); setOpenLines(next);
    if (breakdown[line] && !breakdown[line].error) return;
    setBreakdown(b => ({ ...b, [line]: { loading: true } }));
    loadPnlBreakdown(line, rangeQuery)
      .then(categories => setBreakdown(b => ({ ...b, [line]: { categories } })))
      .catch(e => setBreakdown(b => ({ ...b, [line]: { error: e.message } })));
  };

  const toggleCat = (line, key) => {
    const ck = `${line}|${key}`;
    const next = new Set(openCats);
    if (next.has(ck)) { next.delete(ck); setOpenCats(next); return; }
    next.add(ck); setOpenCats(next);
    if (catTx[ck] && !catTx[ck].error) return;
    setCatTx(t => ({ ...t, [ck]: { loading: true } }));
    loadPnlRows(line, key, rangeQuery)
      .then(transactions => setCatTx(t => ({ ...t, [ck]: { transactions } })))
      .catch(e => setCatTx(t => ({ ...t, [ck]: { error: e.message } })));
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>Loading...</div>;

  const pnlData = pnl?.months || [];
  const totals = pnl?.totals || {};
  // Cash basis since 2026-09-24 (jobq-api kc_pnl.py): `revenue` is money collected, by the date it
  // arrived. It used to be paid-in-full invoices filed by invoice date, which made every recent
  // month look like a collapse. An API still on the old shape has no `basis`; show it unlabelled.
  const isCash = pnl?.basis === "cash";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text, margin: 0 }}>Profit & Loss</h1>
          {isCash && (
            <div style={{ fontSize: 12.5, color: T.textSecondary, marginTop: 4, maxWidth: 760, lineHeight: 1.45 }}>
              Cash basis: revenue is money collected, by the date it arrived, including what Bad Idea settled
              by netting against its own bills to KanCraft. Expenses are what KanCraft paid, on the day it paid:
              Bill.com bills plus checking-account and card spending outside Bill.com. Loan interest is its own
              line. Click Revenue, Invoiced, Operating Expenses or Loan interest to see what is in them.
            </div>
          )}
          {isCash && pnlNotes(pnl).map(n => (
            <div key={n} style={{ fontSize: 12, color: C.amber, marginTop: 4, maxWidth: 760 }}>{n}</div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {PNL_RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              style={{
                padding: "6px 14px", borderRadius: 6, border: "none",
                fontWeight: range === r.key ? 600 : 400,
                background: range === r.key ? C.indigo : T.inputBg,
                color: range === r.key ? "#fff" : T.textMuted,
                cursor: "pointer", fontSize: 13,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <SummaryCard
          T={T}
          label={isCash ? "Revenue (cash collected)" : "Revenue"}
          value={totals.revenue || 0}
          color={C.green}
          subtitle={isCash && totals.collected_offset ? `incl. ${fmt(totals.collected_offset)} netted with Bad Idea` : undefined}
        />
        {isCash && (
          <SummaryCard T={T} label="Invoiced" value={totals.invoiced || 0} color={C.blue} subtitle="non-void invoices, by invoice date" />
        )}
        <SummaryCard
          T={T}
          label="Operating Expenses"
          value={totals.opex || 0}
          color={C.red}
          subtitle={totals.needs_review ? `incl. ${fmt(totals.needs_review)} not yet categorized` : undefined}
        />
        {totals.interest !== undefined && (
          <SummaryCard T={T} label="Loan interest" value={totals.interest || 0} color={C.amber}
            subtitle="estimated from each loan's schedule" />
        )}
        <SummaryCard
          T={T}
          label="Net Profit"
          value={totals.net_profit || 0}
          color={(totals.net_profit || 0) >= 0 ? C.green : C.red}
        />
      </div>

      {/* Monthly bar chart */}
      {pnlData.length > 0 && (
        <div style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow, border: `1px solid ${T.border}`, marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>Monthly Breakdown</h2>
          <div style={{ height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={pnlData}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.gridStroke} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: T.axisColor }} />
                <YAxis tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`} tick={{ fontSize: 12, fill: T.axisColor }} />
                <Tooltip formatter={v => fmt(v)} contentStyle={{ background: T.cardBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text }} />
                <Legend wrapperStyle={{ color: T.textSecondary }} />
                <Bar dataKey="revenue" name={isCash ? "Revenue (collected)" : "Revenue"} fill={C.green} radius={[2, 2, 0, 0]} />
                <Bar dataKey="opex" name="OpEx" fill={C.red} radius={[2, 2, 0, 0]} />
                {totals.interest !== undefined && <Bar dataKey="interest" name="Loan interest" fill={C.amber} radius={[2, 2, 0, 0]} />}
                <Bar dataKey="net_profit" name="Net Profit" fill={C.indigo} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* P&L Table */}
      {pnlData.length > 0 && (
        <div style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow, border: `1px solid ${T.border}`, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${T.borderStrong}` }}>
                <th style={thStyle(T)}>Line Item</th>
                {pnlData.map(m => <th key={m.month} style={{ ...thStyle(T), textAlign: "right" }}>{m.month}</th>)}
                <th style={{ ...thStyle(T), textAlign: "right", fontWeight: 700 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const drill = { T, pnlData, openLines, breakdown, openCats, catTx, toggleLine, toggleCat };
                return (
                  <>
                    {/* The lines, their labels and which open a breakdown: financeData.pnlLines. */}
                    {pnlLines(pnl).map((ln) => {
                      const color = ln.tone === "in" ? C.green : ln.tone === "out" ? C.red : ln.tone === "interest" ? C.amber
                        : ln.tone === "net" ? ((totals.net_profit || 0) >= 0 ? C.green : C.red) : T.textSecondary;
                      return (
                        <React.Fragment key={ln.field}>
                          <PnlRow T={T} label={ln.label} data={pnlData} field={ln.field} color={color} total={totals[ln.field] || 0}
                            bold={ln.bold} muted={ln.muted} expandable={!!ln.drill}
                            expanded={ln.drill ? openLines.has(ln.drill) : undefined}
                            onToggle={ln.drill ? () => toggleLine(ln.drill) : undefined} />
                          {ln.drill && <PnlDrill {...drill} line={ln.drill} />}
                        </React.Fragment>
                      );
                    })}
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}

      {pnlData.length === 0 && (
        <div style={{ padding: 32, textAlign: "center", color: T.textSecondary, background: T.cardBg, borderRadius: 12, border: `1px solid ${T.border}` }}>
          No P&L data available. Ensure invoice payments are recorded and Bill.com bills are synced.
        </div>
      )}
    </div>
  );
}


// ============================================================
// TAB 3: TRANSACTIONS
// ============================================================
// PAGE_SIZE: features/finance/financeData.

function TransactionsTab({ T }) {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadAccounts().then(setAccounts).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadData = useCallback(() => {
    loadTransactions({ page, search: debouncedSearch, startDate, endDate, accountId }) // financeData
      .then(r => { setTransactions(r.rows); if (r.total != null) setTotal(r.total); })
      .catch(() => {});
  }, [page, debouncedSearch, startDate, endDate, accountId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncTransactions(); // financeData
      loadData();
    } catch (e) { /* ignore */ }
    setSyncing(false);
  };

  // Client-side sort on loaded page
  const sorted = sortTransactions(transactions, sortField, sortDir); // financeData

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir(field === "date" ? "desc" : "asc"); }
  };
  const sortIcon = (field) => sortField !== field ? "" : sortDir === "asc" ? " \u2191" : " \u2193";

  const { totalPages, rangeStart, rangeEnd } = pageRange(page, total); // financeData

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text, margin: 0 }}>Bank Transactions</h1>
        <button onClick={handleSync} disabled={syncing} style={btnStyle(C.indigo, syncing)}>
          {syncing ? "Syncing..." : "Sync Transactions"}
        </button>
      </div>

      <div style={{ background: T.cardBg, borderRadius: 12, padding: 20, boxShadow: T.shadow, border: `1px solid ${T.border}` }}>
        {/* Filter bar */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Search by merchant..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: "1 1 200px", maxWidth: 300, padding: "8px 12px",
              border: `1px solid ${T.borderStrong}`, borderRadius: 8, fontSize: 14,
              background: T.inputBg, color: T.text,
            }}
          />
          <select
            value={accountId}
            onChange={e => { setAccountId(e.target.value); setPage(0); }}
            style={{
              padding: "8px 12px", border: `1px solid ${T.borderStrong}`, borderRadius: 8,
              fontSize: 13, background: T.inputBg, color: T.text, minWidth: 180,
            }}
          >
            <option value="">All accounts</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>
                {a.institution_name} — {a.account_name} {a.mask ? `***${a.mask}` : ""}
              </option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(0); }}
              style={dateInputStyle(T)} />
            <span style={{ fontSize: 12, color: T.textSecondary }}>to</span>
            <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(0); }}
              style={dateInputStyle(T)} />
            {(startDate || endDate) && (
              <button onClick={() => { setStartDate(""); setEndDate(""); setPage(0); }}
                style={{ padding: "3px 8px", borderRadius: 4, border: `1px solid ${T.borderStrong}`, fontSize: 11, background: T.inputBg, color: T.textMuted, cursor: "pointer" }}>
                Clear
              </button>
            )}
          </div>
          <span style={{ fontSize: 12, color: T.textSecondary, marginLeft: "auto" }}>
            {total > 0 ? `${rangeStart}\u2013${rangeEnd} of ${total.toLocaleString()}` : "0 transactions"}
          </span>
        </div>

        {sorted.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: T.textSecondary }}>
            {total === 0 && !debouncedSearch && !startDate && !endDate
              ? "No transactions found. Connect a bank account and sync to see transaction history."
              : "No transactions match the current filters."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.borderStrong}` }}>
                  <th style={{ ...thStyle(T), cursor: "pointer" }} onClick={() => handleSort("date")}>Date{sortIcon("date")}</th>
                  <th style={{ ...thStyle(T), cursor: "pointer" }} onClick={() => handleSort("merchant_name")}>Merchant{sortIcon("merchant_name")}</th>
                  <th style={thStyle(T)}>Category</th>
                  <th style={{ ...thStyle(T), cursor: "pointer", textAlign: "right" }} onClick={() => handleSort("amount")}>Amount{sortIcon("amount")}</th>
                  <th style={thStyle(T)}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(txn => (
                  <tr key={txn.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ ...tdStyle(T), whiteSpace: "nowrap" }}>{txn.date}</td>
                    <td style={{ ...tdStyle(T), fontWeight: 500 }}>{txn.merchant_name || txn.name || "-"}</td>
                    <td style={{ ...tdStyle(T), color: T.textMuted, fontSize: 13 }}>
                      {txn.category || "-"}
                      {txn.subcategory && <span style={{ color: T.textSecondary, marginLeft: 4, fontSize: 11 }}>/ {txn.subcategory}</span>}
                    </td>
                    <td style={{
                      ...tdStyle(T), textAlign: "right", fontWeight: 600,
                      color: txn.amount > 0 ? C.red : C.green,
                    }}>
                      {txn.amount > 0 ? "-" : "+"}{fmt(Math.abs(txn.amount))}
                    </td>
                    <td style={tdStyle(T)}>
                      <span style={{
                        display: "inline-block", padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 600,
                        background: txn.pending ? "rgba(251,191,36,0.15)" : "rgba(16,185,129,0.15)",
                        color: txn.pending ? C.amber : C.green,
                      }}>
                        {txn.pending ? "Pending" : "Posted"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={pagBtnStyle(T, page === 0)}>
              &larr; Prev
            </button>
            <span style={{ fontSize: 13, color: T.textMuted }}>Page {page + 1} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={pagBtnStyle(T, page >= totalPages - 1)}>
              Next &rarr;
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================
// SHARED COMPONENTS
// ============================================================
function MetricCard({ T, label, value, color, subtitle }) {
  return (
    <div style={{
      background: T.cardBg, borderRadius: 10, padding: 16, boxShadow: T.shadow,
      border: `1px solid ${T.border}`, borderLeft: `3px solid ${color}`,
      flex: "1 1 170px", minWidth: 170,
    }}>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      {subtitle && <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

function SummaryCard({ T, label, value, color, subtitle }) {
  return (
    <div style={{
      background: T.cardBg, borderRadius: 10, padding: 16, boxShadow: T.shadow,
      border: `1px solid ${T.border}`, borderLeft: `3px solid ${color}`,
      flex: "1 1 200px", minWidth: 200,
    }}>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{fmt(value)}</div>
      {subtitle && <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

function PnlChevron({ T, expandable, expanded }) {
  return (
    <span style={{ display: "inline-block", width: 16, color: T.textMuted }}>
      {expandable ? (expanded ? "\u25BE" : "\u25B8") : ""}
    </span>
  );
}

function PnlRow({ T, label, data, field, color, total, bold, muted, expandable, expanded, onToggle }) {
  // `muted` = a reference line under Revenue (netted amount, invoiced): smaller, indented, not summed.
  // `expandable` lines open a category breakdown (PnlDrill) underneath; the whole row is the toggle.
  const small = muted ? { fontSize: 12 } : {};
  return (
    <tr
      style={{ borderBottom: `1px solid ${T.border}`, cursor: expandable ? "pointer" : "default" }}
      onClick={expandable ? onToggle : undefined}
      title={expandable ? (expanded ? "Collapse" : "Show what is in this line") : undefined}
    >
      <td style={{ ...tdStyle(T), ...small, fontWeight: bold ? 600 : 400, color, ...(muted ? { paddingLeft: 28 } : {}), whiteSpace: "nowrap" }}>
        {muted && !expandable && <span style={{ display: "inline-block", width: 16 }} />}
        {(expandable || !muted) && <PnlChevron T={T} expandable={expandable} expanded={expanded} />}
        {label}
      </td>
      {data.map(m => (
        <td key={m.month} style={{ ...tdStyle(T), ...small, textAlign: "right", fontWeight: bold ? 600 : 400, color: (m[field] || 0) < 0 ? C.red : muted ? T.textSecondary : T.text }}>
          {fmt(m[field] || 0)}
        </td>
      ))}
      <td style={{ ...tdStyle(T), ...small, textAlign: "right", fontWeight: muted ? 500 : 700, color: total < 0 ? C.red : color }}>
        {fmt(total)}
      </td>
    </tr>
  );
}


// Level 1 (categories) and level 2 (transactions) under an expanded P&L line. A category's cells
// come from its by_month; a transaction's amount sits only in its own month's column.
function PnlDrill({ T, pnlData, line, openLines, breakdown, openCats, catTx, toggleLine, toggleCat }) {
  if (!openLines.has(line)) return null;
  const bd = breakdown[line] || { loading: true };
  const cols = pnlData.length + 1;
  if (bd.loading) return <PnlInfoRow T={T} depth={1} cols={cols} text="Loading\u2026" />;
  if (bd.error) return <PnlInfoRow T={T} depth={1} cols={cols} text={`Could not load the breakdown (${bd.error}).`} />;
  if (!bd.categories.length) return <PnlInfoRow T={T} depth={1} cols={cols} text="Nothing in this range." />;
  return bd.categories.map(cat => {
    const ck = `${line}|${cat.key}`;
    const open = openCats.has(ck);
    const tx = catTx[ck];
    return (
      <React.Fragment key={ck}>
        <PnlDrillRow T={T} depth={1} pnlData={pnlData} expandable expanded={open} onToggle={() => toggleCat(line, cat.key)}
          label={`${cat.label}`} count={cat.count} valueFor={m => cat.by_month[m] || 0} total={cat.total}
          warn={!!cat.needs_review} note={cat.needs_review ? "needs a category" : cat.estimated ? "estimated" : undefined} />
        {open && (!tx || tx.loading) && <PnlInfoRow T={T} depth={2} cols={cols} text="Loading\u2026" />}
        {open && tx && tx.error && <PnlInfoRow T={T} depth={2} cols={cols} text={`Could not load the rows (${tx.error}).`} />}
        {open && tx && tx.transactions && tx.transactions.map(t => (
          <PnlDrillRow key={`${ck}|${t.id}`} T={T} depth={2} pnlData={pnlData} small
            label={`${t.date} \u00b7 ${t.label}`} valueFor={m => (t.month === m ? t.amount : 0)} total={t.amount} />
        ))}
      </React.Fragment>
    );
  });
}

function PnlDrillRow({ T, depth, pnlData, label, count, valueFor, total, expandable, expanded, onToggle, small, warn, note }) {
  const size = small ? 12 : 13;
  const base = { ...tdStyle(T), fontSize: size, background: T.inputBg, padding: "6px 12px" };
  return (
    <tr
      style={{ borderBottom: `1px solid ${T.border}`, cursor: expandable ? "pointer" : "default" }}
      onClick={expandable ? onToggle : undefined}
    >
      <td style={{ ...base, paddingLeft: 12 + depth * 20, color: small ? T.textSecondary : T.text, whiteSpace: "nowrap" }}>
        <PnlChevron T={T} expandable={expandable} expanded={expanded} />
        <span style={warn ? { color: C.amber, fontWeight: 600 } : undefined}>{label}</span>
        {count != null && <span style={{ color: T.textMuted, fontSize: 11, marginLeft: 6 }}>({count})</span>}
        {note && <span style={{ color: warn ? C.amber : T.textMuted, fontSize: 11, marginLeft: 6, fontStyle: "italic" }}>{note}</span>}
      </td>
      {pnlData.map(m => {
        const v = valueFor(m.month);
        return (
          <td key={m.month} style={{ ...base, textAlign: "right", color: v < 0 ? C.red : small ? T.textSecondary : T.text }}>
            {v ? fmt(v) : ""}
          </td>
        );
      })}
      <td style={{ ...base, textAlign: "right", fontWeight: 600, color: total < 0 ? C.red : small ? T.textSecondary : T.text }}>
        {fmt(total)}
      </td>
    </tr>
  );
}

function PnlInfoRow({ T, depth, cols, text }) {
  return (
    <tr style={{ borderBottom: `1px solid ${T.border}` }}>
      <td colSpan={cols + 1} style={{ ...tdStyle(T), fontSize: 12, color: T.textMuted, background: T.inputBg, paddingLeft: 12 + depth * 20 + 16 }}>
        {text}
      </td>
    </tr>
  );
}


// ============================================================
// STYLE HELPERS
// ============================================================
function btnStyle(color, disabled) {
  return {
    padding: "8px 16px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600,
    background: disabled ? "#4a5568" : color, color: "#fff",
    cursor: disabled ? "wait" : "pointer", opacity: disabled ? 0.7 : 1,
  };
}

function thStyle(T) {
  return { textAlign: "left", padding: "8px 12px", fontSize: 12, fontWeight: 600, color: T.textMuted, userSelect: "none" };
}

function tdStyle(T) {
  return { padding: "8px 12px", fontSize: 14, color: T.text };
}

function dateInputStyle(T) {
  return { padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, fontSize: 12, color: T.text, background: T.inputBg };
}

function pagBtnStyle(T, disabled) {
  return {
    padding: "6px 14px", borderRadius: 6, border: `1px solid ${T.borderStrong}`, fontSize: 13, fontWeight: 500,
    background: disabled ? T.cardBg : T.inputBg, color: disabled ? T.textMuted : C.indigo,
    cursor: disabled ? "default" : "pointer",
  };
}
